import { filterFilesByMimeTypes } from '@/modules/Project/useSupportedMimeTypes'
import { toast } from '@/shared/toast'
import { getFilesFromDataTransferEvent } from '@/shared/utils/file'
import { useDropZone, type MaybeRefOrGetter, type UseDropZoneOptions } from '@vueuse/core'
import { toValue } from 'vue'

type Options = Omit<UseDropZoneOptions, 'dataTypes' | 'onDrop'> & {
  /**
   * The MIME types that are supported by the dropzone. If undefined,
   * all types are supported. Files that don't match the supported types
   * will be filtered out and the user will see an error toast.
   */
  supportedMimeTypes: MaybeRefOrGetter<string[] | undefined>
  /**
   * Callback called when files are dropped, containing all files that
   * have supported MIME types.
   */
  onAccepted: (files: File[], event: DragEvent) => void
}

/**
 * Wraps the vueuse useDropZone hook, changing how rejected files are handled.
 *
 * The default useDropzone behaviour is to silently ignore all rejected files,
 * and to not put the dropzone into its active state. With this composable,
 * any files will trigger the dropzones active state, and accepted/rejected files
 * are passed into callback functions.
 *
 */
export const useDropzone = (
  target: MaybeRefOrGetter<HTMLElement | Document | null | undefined>,
  options: Options,
) => {
  return useDropZone(target, {
    ...options,
    onDrop: async (_files, event) => {
      /**
       * Call this utility function to get files instead of using the first `onDrop`
       * argument, because we want to extract files from directories.
       */
      const files = await getFilesFromDataTransferEvent(event)

      const { supportedFiles, unsupportedMimeTypes } = filterFilesByMimeTypes(
        files.map(({ file }) => file) || [],
        toValue(options.supportedMimeTypes) || 'all',
      )

      if (unsupportedMimeTypes.length > 0) {
        if (unsupportedMimeTypes.length === 1 && unsupportedMimeTypes[0] === '') {
          toast.error('Unknown file type')
        } else {
          toast.error(`Unsupported file types: ${unsupportedMimeTypes.join(', ')}`)
        }
      }

      options.onAccepted(supportedFiles, event)
    },
  })
}
